import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { bookingWhen, cardPhotoUrl, chargedNow, paymentState, photoUrl } from "@1-now/booking-core";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useData } from "data/data-provider";
import { retriveBooking } from "api/booking/booking";
import CarPhoto from "../BookingSteps/CarPhoto";
import TotalBar from "../BookingSteps/TotalBar";
import { dollars } from "../BookingSteps/dollars";
import { buildPriceDetails } from "../BookingSteps/priceDetails";
import { payByText } from "../BookingSteps/payByDate";
import { payTargetFor } from "./payTarget";

/** How often, and how many times, the page asks for the payment link the backend makes after the booking. */
export const LINK_POLL_MS = 3000;
export const LINK_POLL_TRIES = 12;
/** How long "Pay and book" waits for the browser to leave for the payment page before it says it could not. */
export const OPEN_WAIT_MS = 5000;

/**
 * "Your car is reserved" (1NOW-11156): where a pay-later booking lands, instead
 * of the booking page, until it is paid. It shows the car, the booking number,
 * what is due and by when, and "Pay and book" opens the booking's own payment link
 * (the one the email and the text carry). Once paid, the renter goes on to the
 * booking page as before.
 *
 * Reads the booking by its uuid with the renter's own session. The link is made
 * by the backend after the booking is saved, so it may not be there on the
 * first read: the page asks again a few times and says so meanwhile.
 */
const ReservedPage: FC = () => {
  const [params] = useSearchParams();
  const id = params.get("id");
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingdetails, ourCompany } = useData();

  const seed =
    (location.state as any)?.booking?.unique_identifier === id
      ? (location.state as any).booking
      : bookingdetails?.unique_identifier === id
        ? bookingdetails
        : null;
  const [booking, setBooking] = useState<any>(seed);
  const [missing, setMissing] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  // "Pay and book" has been pressed and the browser has not left yet / has not been able to (1NOW-11206).
  const [opening, setOpening] = useState(false);
  const [stuck, setStuck] = useState(false);
  const giveUpOpening = useRef<number>();

  useEffect(() => {
    if (!id) return;
    let live = true;
    let tries = 0;
    let timer: number | undefined;
    const look = async () => {
      // The call is typed { data }, but answers with the booking itself (or { error, status }).
      const read: any = await retriveBooking(id);
      if (!live) return;
      tries += 1;
      if (read && !read.error && read.id != null) {
        setBooking(read);
        setMissing(false);
        if (payTargetFor(read) || paymentState(read).status === "paid") return;
      } else if (read?.status === 404 || read?.status === 401) {
        // Not a booking this session can read, and not one that will become one.
        if (!seed) return setMissing(true);
      }
      if (tries >= LINK_POLL_TRIES) return setGaveUp(true);
      timer = window.setTimeout(look, LINK_POLL_MS);
    };
    look();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [id]);

  // Back here from the payment page (the browser kept this page): a fresh Pay and book, not a stuck one.
  useEffect(() => {
    const back = () => {
      window.clearTimeout(giveUpOpening.current);
      setOpening(false);
      setStuck(false);
    };
    window.addEventListener("pageshow", back);
    return () => {
      window.removeEventListener("pageshow", back);
      window.clearTimeout(giveUpOpening.current);
    };
  }, []);

  // Paid (the renter came back here after paying): the booking is where they belong now.
  const status = paymentState(booking).status;
  useEffect(() => {
    if (id && (status === "paid" || status === "partially-paid"))
      navigate(`/booking-details?id=${id}`, { replace: true });
  }, [id, status]);

  if (!id || missing)
    return (
      <div className="container mx-auto max-w-md px-4 py-16 text-center text-text-color">
        <p className="text-lg font-extrabold">We couldn&apos;t find that booking</p>
        <p className="mt-2 text-sm opacity-70">Open the payment link from your email or text, or start again.</p>
        <button
          type="button"
          onClick={() => navigate("/vehicles")}
          className="mt-6 inline-flex min-h-[44px] items-center rounded-full bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary px-6 py-3 text-sm font-extrabold text-on-button"
        >
          Browse cars
        </button>
      </div>
    );

  const target = payTargetFor(booking);
  const amount = target?.amount ?? chargedNow(booking?.invoice) ?? (Number(booking?.final_price_without_security) || null);
  // 1NOW-11200: the refundable deposit is held on its own, outside the amount due; say so, as checkout does.
  const deposit = booking?.invoice ? buildPriceDetails(booking.invoice).deposit : null;
  const due = payByText(booking);
  const pick = bookingWhen(booking?.pick_up_time, ourCompany);
  const drop = bookingWhen(booking?.drop_off_time, ourCompany);
  const photo = cardPhotoUrl(booking?.fleet) ?? photoUrl(booking?.fleet?.fleet_photos?.[0]?.photo_url);
  const cancelled = booking?.trip_status === "cancelled";
  const preparing = !target && !cancelled && !gaveUp;
  /**
   * Leaves for the payment link. The page used to call location.assign and say nothing more, so when the
   * browser did not go (blocked, a link that never loads) the renter saw a button that did nothing
   * (1NOW-11206). Now it shows that it is opening, and if this page is still here after OPEN_WAIT_MS it says
   * so and offers the same link as a plain link.
   */
  const pay = () => {
    if (!target || opening) return;
    setStuck(false);
    setOpening(true);
    window.clearTimeout(giveUpOpening.current);
    const fail = () => {
      setOpening(false);
      setStuck(true);
    };
    giveUpOpening.current = window.setTimeout(fail, OPEN_WAIT_MS);
    try {
      window.location.assign(target.url);
    } catch {
      window.clearTimeout(giveUpOpening.current);
      fail();
    }
  };
  const label = preparing ? "Preparing your link" : opening ? "Opening payment…" : "Pay and book";

  return (
    <div className="container mx-auto max-w-md px-4 pb-32 pt-8 text-text-color lg:pb-12">
      {/* 1NOW-11199: the tick and the heading are one confirmation, on one line. */}
      <div className="mb-4 flex items-center gap-3">
        {!cancelled && (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700" aria-hidden="true">
            <CheckIcon className="h-6 w-6" />
          </span>
        )}
        <h1 className="text-2xl font-extrabold uppercase leading-tight">
          {cancelled ? "This reservation was cancelled" : "Your car is reserved"}
        </h1>
      </div>

      {booking ? (
        <div className="flex items-center gap-3 rounded-2xl border border-secondary-100 bg-card-color p-3">
          <CarPhoto photo={photo} className="h-12 w-[72px] rounded-lg" />
          <div className="min-w-0">
            <b className="block truncate">{booking?.fleet?.name}</b>
            <span className="block text-xs opacity-70">
              {pick && drop ? `${pick.date} – ${drop.date} · ` : ""}Booking #{booking?.id}
            </span>
          </div>
        </div>
      ) : (
        <div className="h-[72px] animate-pulse rounded-2xl bg-secondary-100/60" aria-hidden="true" />
      )}

      {!cancelled && (
        <div className="mt-3 rounded-2xl border border-secondary-100 bg-card-color p-4">
          <span className="text-xs opacity-70">Amount due</span>
          <div className="text-3xl font-extrabold tabular-nums" data-testid="amount-due">
            {amount === null ? "—" : dollars(amount)}
          </div>
          {due && (
            <p className="mt-1 text-sm opacity-80">
              Pay {amount === null ? "" : `${dollars(amount)} `}by {due} from the link we send you. If it isn&apos;t paid by
              then, the reservation is released.
            </p>
          )}
          {deposit !== null && (
            <div className="mt-3 rounded-xl bg-secondary-100/40 p-3 text-sm" data-testid="reserved-deposit">
              <b>Refundable security deposit {dollars(deposit)}</b>
              <p className="text-xs opacity-70">Held on your card during the trip, not part of the amount due.</p>
            </div>
          )}
          <p className="mt-3 text-xs opacity-70">We&apos;ve also emailed and texted you the payment link.</p>
          {gaveUp && !target && (
            <p className="mt-3 text-xs font-semibold">
              Your link is not ready on this page yet. Use the one we emailed and texted you.
            </p>
          )}
          {stuck && target && (
            <p role="alert" className="mt-3 text-xs font-semibold" data-testid="pay-stuck">
              We couldn&apos;t open the payment page.{" "}
              <a href={target.url} target="_blank" rel="noopener noreferrer" className="underline">
                Open it here
              </a>
              , or use the link we emailed and texted you.
            </p>
          )}
          <button
            type="button"
            onClick={pay}
            disabled={!target || opening}
            className="mt-4 hidden min-h-[48px] w-full items-center justify-center rounded-full bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary px-6 py-3 text-sm font-extrabold text-on-button disabled:opacity-60 lg:inline-flex"
          >
            {label}
          </button>
        </div>
      )}
      <p className="mt-4 text-xs opacity-70">After you pay, you&apos;ll see your booking and confirm your ID and insurance.</p>

      {!cancelled && (
        <div className="lg:hidden">
          <TotalBar
            total={amount}
            note={due ? `due by ${due.replace(/^\w+, /, "")}` : undefined}
            label={label}
            onClick={pay}
            disabled={!target}
            busy={preparing || opening}
            fixed
          />
        </div>
      )}
    </div>
  );
};

export default ReservedPage;
